<template>
    <div class="image-wrapper-skeleton" v-if="!isLoaded"></div>
    <img v-bind="$attrs" class="image-wrapper-img" v-show="isLoaded" :src="optimizedSrc" :alt="alt" @load="onImageLoad"
        @click="openFullscreen" />
    <div v-if="isFullscreen" class="fullscreen-modal" @click="closeFullscreen">
        <div class="fullscreen-content" @click.stop>
            <img :src="optimizedSrc" :alt="alt" class="fullscreen-image" />
        </div>
    </div>
</template>

<style scoped>
.image-wrapper-skeleton {
    width: 100%;
    height: 100%;
    background-color: var(--background-color);
    background-image: linear-gradient(90deg,
            var(--secondary-background-color) 0px,
            var(--secondary-background-color) 41px,
            var(--tertiary-background-color) 42px,
            var(--tertiary-background-color) 80px,
            var(--secondary-background-color) 81px);
    background-size: 200% 100%;
    animation: shimmer 3.8s infinite linear;
}

.image-wrapper-img {
    cursor: pointer;
}

@keyframes shimmer {
    0% {
        background-position: 200% 0;
    }

    100% {
        background-position: -200% 0;
    }
}

.fullscreen-modal {
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    background-color: var(--background-color);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 9999;
    animation: fadeIn 0.3s ease;
}

.fullscreen-content {
    position: relative;
    max-width: 90vw;
    max-height: 90vh;
    display: flex;
    align-items: center;
    justify-content: center;
}

.fullscreen-image {
    max-width: 90vw;
    max-height: 90vh;
    object-fit: contain;
}

@keyframes fadeIn {
    from {
        opacity: 0;
    }

    to {
        opacity: 1;
    }
}

@media (max-width: 581px) {
    .image-wrapper-img {
        cursor: default;
    }
}

@media (max-width: 768px) {
    .fullscreen-content {
        max-width: 95vw;
        max-height: 95vh;
    }
}
</style>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue';
import imageUrlBuilder from '@sanity/image-url'
import { sanityClient } from '@/sanity/client';

const isLoaded = ref(false)
const isFullscreen = ref(false)
const builder = imageUrlBuilder(sanityClient)

const props = defineProps({ src: String, alt: String, denyFullscreen: Boolean })

function urlFor(source) {
    return builder
        .image(source)
        .fit('min')
        .auto('format')
}

const optimizedSrc = computed(() => props.src ? urlFor(props.src).url() : '')

function onImageLoad() {
    isLoaded.value = true;
};

function openFullscreen() {
    if (props.denyFullscreen) {
        return
    }

    if (window.innerWidth < 581) {
        return
    }

    isFullscreen.value = true;
    document.body.style.overflow = 'hidden'; // Prevent background scrolling
}

function closeFullscreen() {
    isFullscreen.value = false;
    document.body.style.overflow = ''; // Restore scrolling
}

function handleKeydown(event) {
    if (event.key === 'Escape' && isFullscreen.value) {
        closeFullscreen();
    }
}

onMounted(() => {
    document.addEventListener('keydown', handleKeydown);
});

onUnmounted(() => {
    document.removeEventListener('keydown', handleKeydown);
    document.body.style.overflow = ''; // Ensure scrolling is restored
});
</script>
